import React, { useEffect, useState } from 'react';
import { 
  CircularProgress, 
  IconButton, 
  Table, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Tooltip,
} from '@mui/material';
import { Print } from '@mui/icons-material';
import { useMutation } from 'react-query';
import usePrint from '@hooks/usePrint';
import CustomTableBody from '../../../utilis/component/CustomTableBody';
import { getRadiologyDetails } from './services';
import { printReport } from '../../../MainDashboard/Radiology/Services';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';

// Interfaces for type safety
interface RadiologyItem {
  cpt_name: string;
  isPerformed: boolean;
  createdByDate: string;
  radiologyId: number;
}

interface RadiologyDetailsResponse {
  response: RadiologyItem[];
}

interface RadiologyDetailsProps {
  id: number;
}

// Constants
const TABLE_HEADINGS = ['Sl no', 'Name',  "Is performed", 'Created date', 'Actions'];

const RadiologyDetails: React.FC<RadiologyDetailsProps> = ({ id }) => {
  // Data fetching mutation
  const { mutate: fetchDetails, data, isLoading } = useMutation<RadiologyDetailsResponse, Error, number>(
    getRadiologyDetails
  );

  const [printingIds, setPrintingIds] = useState<number[]>([]);

  const { printPdf } = usePrint();

  useEffect(() => {
    if (id) {
      fetchDetails(id);
    }
  }, [id, fetchDetails]);

  const { mutate: getprint } = useMutation(
    printReport,
    {
      onMutate: (radiologyId: number) => {
        // Add the ID to printing state when mutation starts
        setPrintingIds(prev => [...prev, radiologyId]);
      },
      onSuccess: (data) => {
        printPdf(data?.response, "new-tab");
      },
      onSettled: (_data, _error, radiologyId) => {
        // Remove the ID from printing state when mutation completes
        setPrintingIds(prev => prev.filter(id => id !== radiologyId));
      }
    }
  );

  const handlePrint = (radiologyId: number) => {
    getprint(radiologyId);
  };

  const isPrinting = (radiologyId: number) => printingIds.includes(radiologyId);

  const hasData = !!data?.response?.length;

  return (
    <TableContainer className="min-w-[50vw] border">
      <Table size="small" aria-label="radiology details table">
        <TableHead>
          <TableRow className="bg-gray-50">
            {TABLE_HEADINGS.map((heading) => (
              <TableCell 
                key={heading} 
                className="font-bold"
              >
                {heading}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        
        <CustomTableBody 
          alert="No radiology records found"
          dataLoading={isLoading}
          isData={hasData}
          totalSpan={{ col: TABLE_HEADINGS.length, row: 5 }}
        >
          {data?.response.map((item, index) => (
            <TableRow 
              key={`${item.radiologyId}-${index}`}
              className="hover:bg-gray-50 transition-colors"
            >
              <TableCell className="py-3">{index + 1}</TableCell>
              
              <TableCell className="py-3 font-medium">
                {item.cpt_name}
              </TableCell>
              
              <TableCell className="py-3">
                 {item.isPerformed ? <CheckIcon color="success"/> : <CloseIcon color="warning"/>}
              </TableCell>
              
              <TableCell className="py-3 text-gray-600">
                {item?.createdByDate ? moment(item?.createdByDate).format("DD-MM-YYYY hh:ss A") : ""}
              </TableCell>
              
              <TableCell>
                {item.isPerformed && (
                  <Tooltip title="Print Report">
                    <IconButton
                      onClick={() => handlePrint(item.radiologyId)}
                      disabled={isPrinting(item.radiologyId)}
                    >
                      {isPrinting(item.radiologyId) ? (
                        <CircularProgress size={20} />
                      ) : (
                        <Print color="success" />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
        </CustomTableBody>
      </Table>
    </TableContainer>
  );
};

export default RadiologyDetails;